import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AlertStyleDirective } from '../../../directives/alert-style.directive';
import { NgClass, NgIf } from '@angular/common';
import { Severity } from '../../../models/severity';

@Component({
  selector: 'app-import-preview-box',
  standalone: true,
  imports: [AlertStyleDirective, NgIf, NgClass],
  templateUrl: './import-preview-box.component.html',
  styleUrl: './import-preview-box.component.scss',
})
export class ImportPreviewBoxComponent {
  @Input() key: Severity = 'info';
  @Input() numberOfEntities = 0;
  @Output() itemsToShow = new EventEmitter<Severity>();

  /**
   * Emits the category of items to show.
   * @param key - The category of items to show.
   */
  setItemsToShow(key: Severity): void {
    this.itemsToShow.emit(key);
  }

  /**
   * Retrieves the label string corresponding to the current key value.
   * The returned label depends on the `key` property of the object:
   * - 'success': returns 'Neu'
   * - 'info': returns 'Aktualisiert'
   * - 'warning': returns 'Warnung'
   * - 'error': returns 'Fehler'
   * - 'lock': returns 'Gesperrt'
   * - 'secondary': returns 'Nicht gefundene Indikatoren'
   * - Any other value defaults to returning 'Aktualisiert'.
   *
   * @return {string} The label associated with the current key value of the object.
   */
  get labelByKey(): string {
    switch (this.key) {
      case 'success':
        return 'Neu';
      case 'info':
        return 'Aktualisiert';
      case 'warning':
        return 'Warnung';
      case 'error':
        return 'Fehler';
      case 'lock':
        return 'Gesperrt';
      case 'secondary':
        return 'Nicht gefundene Indikatoren';
      default:
        return 'Aktualisiert';
    }
  }

  /**
   * Retrieves the icon class string corresponding to the current key property.
   * The method maps predefined keys to specific icon class strings.
   *
   * @return {string} Returns the icon class string corresponding to the key. If the key does not match any predefined case, it defaults to 'info'.
   */
  get iconByKey(): string {
    switch (this.key) {
      case 'success':
        return 'fas fa-circle-plus';
      case 'info':
        return 'fas fa-save';
      case 'warning':
        return 'fas fa-exclamation-triangle';
      case 'error':
        return 'fas fa-exclamation-circle';
      case 'lock':
        return 'fas fa-lock';
      case 'secondary':
        return 'fas fa-ban';
      default:
        return 'info';
    }
  }
}
